// ===== Layer 2: 카테고리 설정 =====

import type {
	CodeType,
	ItemSearchFilters,
	CustomerSearchFilters,
	InspectPopupSearchFilters,
	LotReelPopupSearchFilters,
} from "src/types/base";

import { getItemPopupList } from "src/apis/itemApi";
import { getCustomerList } from "src/apis/customerApi";
import { getOrganizationList } from "src/apis/organizaApi";
import { getEmployeeList } from "src/apis/employeeApi";
import { getUserBySearch } from "src/apis/authApi";
import { getMenuList } from "src/apis/menuApi";
import {
	getAfeederCodeList,
	getBfeederCodeList,
	getCfeederCodeList,
	getMinorCodeList,
} from "src/apis/commonCodeApi";
import { getWarehouseList } from "src/apis/warehouseApi";
import { getWorkPlaceList } from "src/apis/workPlaceApi";
import { getBankacctList } from "src/apis/bankacctApi";
import { getEquipmentPopupList } from "src/apis/equipmentApi";
import { getInspectPopupList } from "src/apis/inspectApi";
import { getCompanyList } from "src/apis/companyApi";
import { getBomChildItemList, getBomParentItemList } from "src/apis/bomApi";
import { getProductWorkAllList, getProductWorkList } from "src/apis/productWorkApi";
import { getLotSearchList } from "src/apis/lotApi";
import { getProductOrderPopupList } from "src/apis/productOrderApi";
import { getProductResultPopupList } from "src/apis/productResultApi";
import { getWmsOrderInPopupList, getWmsOrderOutPopupList } from "src/apis/wmsOrderApi";

export interface RendererConfig {
	type: "commonCode";
	listKey: string;
	codeField?: string;
	nameField?: string;
}

export interface ColumnDefinition {
	headerName: string;
	field: string;
	flex?: number;
	width?: number;
	cellRenderer?: string;
	rendererConfig?: RendererConfig;
}

export interface ModalSizeOptions {
	size?: "xs" | "sm" | "md" | "lg" | "half" | "mmd";
	width?: number;
}

export type CommonCodeStoreType = {
	unitList?: Array<{ minorCd: string; codeNm: string }>;
	itemAccountList?: Array<{ minorCd: string; codeNm: string }>;
	[key: string]: any;
};

export interface SearchOption {
	key: string;
	label: string;
	type: "input" | "dropdown" | "dateRange";
	placeholder?: string;
	dataKey?: string;
	defaultValue?: string;
	defaultDateRange?: 'currentMonth';
	wrapperOption?: {
		wrapperStyle: string;
		titleStyle: string;
		inputStyle: string;
	};
}

export interface CategoryDefinition {
	type: CodeType;
	titles: readonly string[];
	apiFunction: (...args: any[]) => Promise<any>;
	codeField: string | string[];
	nameField?: string;
	getApiParams: (searchText: string, filters: any) => any[];
	transformResponse?: (data: any[]) => any[];
	searchFields: readonly string[];
	columns:
		| readonly ColumnDefinition[]
		| ((commonCodeStore?: CommonCodeStoreType) => ColumnDefinition[]);
	searchOptions?: readonly SearchOption[];
	searchOptionsPerRow?: number;
	modalSize?: ModalSizeOptions;
	emptyMessage?: string;
}

export const CATEGORIES: readonly CategoryDefinition[] = [
	{
		type: "wmsOrderOut",
		titles: ["출고지시", "출고지시번호"] as const,
		apiFunction: getWmsOrderOutPopupList,
		codeField: "wmsOrderNo",
		nameField: "salesOrderNo",
		getApiParams: (searchText: string) => [searchText],
		searchFields: ["wmsOrderNo", "salesOrderNo", "customCd", "customNm"] as const,
		searchOptions: [
			{
				key: "search",
				label: "검색어",
				type: "input",
				placeholder: "입고지시번호, 수주번호 또는 거래처",
			},
		],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "입고지시번호", field: "wmsOrderNo", width: 150 },
			{ headerName: "사업장", field: "compNm", width: 120 },
			{ headerName: "수주번호", field: "salesOrderNo", width: 150 },
			{ headerName: "요청일", field: "orderDt", width: 110 },
			{ headerName: "처리기한", field: "deadlineDt", width: 110 },
			{ headerName: "거래처코드", field: "customCd", width: 110 },
			{ headerName: "거래처명", field: "customNm", width: 180 },
		] as const,
		modalSize: { size: "md", width: 1150 },
		emptyMessage: "조회 가능한 입고지시가 없습니다.",
	},
	{
		type: "wmsOrderIn",
		titles: ["입고지시", "입고지시번호"] as const,
		apiFunction: getWmsOrderInPopupList,
		codeField: "wmsOrderNo",
		nameField: "purchaseOrderNo",
		getApiParams: (searchText: string) => [searchText],
		searchFields: ["wmsOrderNo", "purchaseOrderNo", "customCd", "customNm"] as const,
		searchOptions: [
			{
				key: "search",
				label: "검색어",
				type: "input",
				placeholder: "입고지시번호, 발주번호 또는 거래처",
			},
		],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "입고지시번호", field: "wmsOrderNo", width: 150 },
			{ headerName: "사업장", field: "compNm", width: 120 },
			{ headerName: "발주번호", field: "purchaseOrderNo", width: 150 },
			{ headerName: "요청일", field: "orderDt", width: 110 },
			{ headerName: "처리기한", field: "deadlineDt", width: 110 },
			{ headerName: "거래처코드", field: "customCd", width: 110 },
			{ headerName: "거래처명", field: "customNm", width: 180 },
		] as const,
		modalSize: { size: "md", width: 1150 },
		emptyMessage: "입고 가능한 입고지시가 없습니다.",
	},

	{
		type: "item",
		titles: ["품목", "품목코드"] as const,
		apiFunction: getItemPopupList,
		codeField: "itemCd",
		nameField: "itemNm",
		getApiParams: (searchText: string, filters: ItemSearchFilters) => [
			searchText,
			filters.itemAccount || "all",
			filters.itemGroup || "all",
			filters.brandCd || "all",
		],
		searchFields: ["itemNm", "itemCd", "modelNo", "itemSpec", "itemUnit"] as const,
		searchOptions: [
			{
				key: "search",
				label: "검색어",
				type: "input",
				placeholder: "코드 또는 품명",
				wrapperOption: {
					wrapperStyle: "w-[340px] min-w-[340px] mb-[5px]",
					titleStyle: "w-[100px] min-w-[100px]",
					inputStyle: "w-[230px] min-w-[230px]",
				},
			},
			{
				key: "itemAccount",
				label: "품목계정",
				type: "dropdown",
				dataKey: "itemAccountList",
				defaultValue: "all",
				wrapperOption: {
					wrapperStyle: "mb-[5px]",
					titleStyle: "w-[80px] min-w-[80px] text-center pl-0",
					inputStyle: "w-[100px] min-w-[100px]",
				},
			},
			{
				key: "itemGroup",
				label: "품목그룹",
				type: "dropdown",
				dataKey: "itemGroupList",
				defaultValue: "all",
				wrapperOption: {
					wrapperStyle: "mb-[5px]",
					titleStyle: "w-[80px] min-w-[80px] text-center pl-0",
					inputStyle: "w-[100px] min-w-[100px]",
				},
			},
			{
				key: "brandCd",
				label: "브랜드",
				type: "dropdown",
				dataKey: "brandCdList",
				defaultValue: "all",
				wrapperOption: {
					wrapperStyle: "mb-[5px]",
					titleStyle: "w-[80px] min-w-[80px] text-center pl-0",
					inputStyle: "w-[100px] min-w-[100px]",
				},
			},
		],
		columns: (commonCodeStore?: CommonCodeStoreType) => {
			const baseColumns: ColumnDefinition[] = [
				{ headerName: "순번", field: "rowIndex", width: 60 },
				{ headerName: "코드", field: "itemCd", flex: 1 },
				{ headerName: "품명", field: "itemNm", flex: 1 },
				{ headerName: "규격", field: "itemSpec", flex: 1 },
				{ headerName: "모델명", field: "modelNo", flex: 1 },
			];

			if (commonCodeStore?.unitList && commonCodeStore.unitList.length > 0) {
				baseColumns.push({
					headerName: "단위",
					field: "itemUnit",
					flex: 1,
					cellRenderer: "commonCodeRenderer",
					rendererConfig: { type: "commonCode", listKey: "unitList", codeField: "minorCd", nameField: "codeNm" },
				});
			} else {
				baseColumns.push({ headerName: "단위", field: "itemUnit", flex: 1 });
			}

			baseColumns.push({
				headerName: "품목계정",
				field: "itemAccount",
				flex: 1,
				cellRenderer: "commonCodeRenderer",
				rendererConfig: {
					type: "commonCode",
					listKey: "itemAccountList",
					codeField: "minorCd",
					nameField: "codeNm",
				},
			});

			return baseColumns;
		},
		modalSize: { size: "lg", width: 1200 },
	},

	{
		type: "lot_reel",
		titles: ["Lot_Reel"] as const,
		apiFunction: getLotSearchList,
		codeField: "lotNo",
		nameField: "itemNm",
		getApiParams: (searchText: string, filters: LotReelPopupSearchFilters) => [
			searchText,
			filters.workplaceCd || "all",
			filters.itemCd || ""
		],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				lotNo: item.lotNo,
				itemcd: item.itemCd,
				itemNm: item.itemNm,
				lotQty: item.lotQty,
			})),
		searchFields: ["lotNo", "itemcd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "LOT No", field: "lotNo", width: 170 },
			{ headerName: "품목코드", field: "itemcd", width: 150 },
			{ headerName: "품명", field: "itemNm", flex: 1 },
			{ headerName: "재고량", field: "lotQty", width: 100 },
		] as const,
		modalSize: { size: "sm", width: 900 },
	},

	{
		type: "bom_childItemcd",
		titles: ["bom_childItemcd", "BOM_자품목"] as const,
		apiFunction: getBomChildItemList,
		codeField: "childItemcd",
		nameField: "itemNm",
		getApiParams: (prntItemcd: string) => [prntItemcd],
		searchFields: ["childItemcd", "itemNm", "itemSpec", "itemUnit"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "코드", field: "childItemcd", flex: 1 },
			{ headerName: "품명", field: "itemNm", flex: 1 },
			{ headerName: "규격", field: "itemSpec", flex: 1 },
			{ headerName: "단위", field: "itemUnit", flex: 1 },
		] as const,
		modalSize: { size: "lg", width: 1200 },
	},
	{
		type: "bom_prntItemcd",
		titles: ["bom_prntItemcd", "BOM_모품목"] as const,
		apiFunction: getBomParentItemList,
		codeField: "prntItemcd",
		nameField: "itemNm",
		getApiParams: (prntItemcd: string) => [prntItemcd],
		searchFields: ["prntItemcd", "itemNm", "itemSpec", "itemUnit"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "코드", field: "prntItemcd", flex: 1 },
			{ headerName: "품명", field: "itemNm", flex: 1 },
			{ headerName: "규격", field: "itemSpec", flex: 1 },
			{ headerName: "단위", field: "itemUnit", flex: 1 },
		] as const,
		modalSize: { size: "lg", width: 1200 },
	},

	{
		type: "customer",
		titles: ["거래처"] as const,
		apiFunction: getCustomerList,
		codeField: "customCd",
		nameField: "customNm",
		getApiParams: (searchText: string, filters: CustomerSearchFilters) => [
			filters.areaCd || "all",
			searchText || "all",
		],
		searchFields: ["customNm", "customCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "거래처", field: "customCd", flex: 1 },
			{ headerName: "상호", field: "customNm", flex: 2 },
			{ headerName: "대표자", field: "owner", flex: 1 },
			{ headerName: "전화번호", field: "telNo", flex: 2 },
			{ headerName: "주소", field: "address", flex: 2 },
		] as const,
		modalSize: { size: "md", width: 1200 },
	},

	{
		type: "organization",
		titles: ["부서", "조직", "상위부서", "통합팀"] as const,
		apiFunction: getOrganizationList,
		codeField: "orgCd",
		nameField: "orgNm",
		getApiParams: (searchText: string) => [searchText],
		searchFields: ["orgNm", "orgCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "부서코드", field: "orgCd", width: 100 },
			{ headerName: "부서명", field: "orgNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 1200 },
	},

	{
		type: "employee",
		titles: ["부서장", "사원번호", "사원", "작업자", "담당자", "발주자", "시험자", "등록자"] as const,
		apiFunction: getEmployeeList,
		codeField: "empNo",
		nameField: "empNm",
		getApiParams: (searchText: string) => [searchText],
		searchFields: ["empNo", "empNm"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "사원번호", field: "empNo", width: 100 },
			{ headerName: "사원명", field: "empNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 400 },
	},

	{
		type: "user",
		titles: ["사용자"] as const,
		apiFunction: getUserBySearch,
		codeField: "userId",
		nameField: "userNm",
		getApiParams: (searchText: string) => [searchText],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				userId: item.userId,
				userNm: item.userNm,
				empNo: item.empNo,
				empNm: item.empNm || "",
			})),
		searchFields: ["userId", "userNm", "empNo", "empNm"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "사용자ID", field: "userId", flex: 1 },
			{ headerName: "사용자명", field: "userNm", flex: 1 },
			{ headerName: "사원번호", field: "empNo", flex: 1 },
			{ headerName: "직원명", field: "empNm", flex: 1 },
		] as const,
		modalSize: { size: "md", width: 1200 },
	},

	{
		type: "menu",
		titles: ["메뉴", "상위메뉴코드"] as const,
		apiFunction: getMenuList,
		codeField: "menuCd",
		nameField: "menuNm",
		getApiParams: (searchText: string) => [searchText],
		searchFields: ["menuNm", "menuCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "메뉴코드", field: "menuCd", width: 100 },
			{ headerName: "메뉴명", field: "menuNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},

	{
		type: "menuAuth",
		titles: ["메뉴권한"] as const,
		apiFunction: getMinorCodeList,
		codeField: "minorCd",
		nameField: "codeNm",
		getApiParams: (searchText: string) => ["AUTGROUP", searchText],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				minorCd: item.minorCd,
				codeNm: item.codeNm,
			})),
		searchFields: ["codeNm", "minorCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "권한코드", field: "minorCd", width: 100 },
			{ headerName: "권한명", field: "codeNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 1000 },
	},

	{
		type: "workplace",
		titles: ["공정코드"] as const,
		apiFunction: getWorkPlaceList,
		codeField: "workplaceCd",
		nameField: "workplaceNm",
		getApiParams: (searchText: string) => [searchText],
		transformResponse: (data: any[]) =>
			data.map((item, index) => ({
				rowIndex: index + 1,
				workplaceCd: item.workplaceCd,
				workplaceNm: item.workplaceNm,
			})),
		searchFields: ["workplaceNm", "workplaceCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "공정코드", field: "workplaceCd", width: 100 },
			{ headerName: "공정명", field: "workplaceNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},

	{
		type: "warehouse",
		titles: ["창고코드"] as const,
		apiFunction: getWarehouseList,
		codeField: "warehouseCd",
		nameField: "warehouseNm",
		getApiParams: (searchText: string) => [searchText],
		transformResponse: (data: any[]) =>
			data.map((item, index) => ({
				rowIndex: index + 1,
				warehouseCd: item.warehouseCd,
				warehouseNm: item.warehouseNm,
			})),
		searchFields: ["warehouseNm", "warehouseCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "창고코드", field: "warehouseCd", width: 100 },
			{ headerName: "창고명", field: "warehouseNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},

	{
		type: "bankacct",
		titles: ["계좌코드", "계좌번호"] as const,
		apiFunction: getBankacctList,
		codeField: "bankAcctCd",
		nameField: "bankAcctNm",
		getApiParams: (searchText: string) => [searchText || ""],
		searchFields: ["bankAcctNm", "bankAcctCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "계좌코드", field: "bankAcctCd", flex: 1 },
			{ headerName: "계좌명", field: "bankAcctNm", flex: 2 },
			{ headerName: "계좌번호", field: "bankAcctNo", flex: 2 },
			{ headerName: "은행", field: "bankCd", flex: 2 },
		] as const,
		modalSize: { size: "md", width: 800 },
	},

	{
		type: "equipment",
		titles: ["설비코드"] as const,
		apiFunction: getEquipmentPopupList,
		codeField: "equipmentCd",
		nameField: "equipmentNm",
		getApiParams: (searchText: string) => [searchText],
		searchFields: ["equipmentNm", "equipmentCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "설비코드", field: "equipmentCd", width: 100 },
			{ headerName: "설비명", field: "equipmentNm", flex: 1 },
			{ headerName: "설비규격", field: "equipmentSpec", flex: 1 },
			{ headerName: "설비유형", field: "equipmentType", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},

	{
		type: "inspect",
		titles: ["설비점검코드"] as const,
		apiFunction: getInspectPopupList,
		codeField: "inspectCd",
		nameField: "inspectNm",
		getApiParams: (searchText: string, filters: InspectPopupSearchFilters) => [
			searchText,
			filters.inspectMethod || "all",
			filters.inspectNm || "all",
			filters.inspectType || "all",
			filters.eqmType || "all",
		],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				eqmType: item.eqmType,
				inspectCd: item.inspectCd,
				inspectNm: item.inspectNm,
				inspectMethod: item.inspectMethod,
				inspectType: item.inspectType,
				inspectValue: item.inspectValue,
				remark: item.remark,
			})),
		searchFields: ["inspectNm", "inspectCd", "inspectMethod", "inspectType", "eqmType"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "설비유형", field: "eqmType", width: 100 },
			{ headerName: "점검코드", field: "inspectCd", width: 100 },
			{ headerName: "점검명", field: "inspectNm", flex: 1 },
			{ headerName: "점검방법", field: "inspectMethod", flex: 1 },
			{ headerName: "점검유형", field: "inspectType", flex: 1 },
			{ headerName: "결과유형", field: "inspectValue", flex: 1 },
			{ headerName: "비고", field: "remark", flex: 1 },
		] as const,
	},

	{
		type: "company",
		titles: ["사업장", "사업장명"] as const,
		apiFunction: getCompanyList,
		codeField: "compCd",
		nameField: "compNm",
		getApiParams: (searchText: string) => [searchText || ""],
		searchFields: ["compNm", "compCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "사업장코드", field: "compCd", width: 100 },
			{ headerName: "사업장명", field: "compNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 1400 },
	},
	{
		type: 'productOrder',
		titles: ['작업지시번호', '생산지시번호'] as const,

		apiFunction: getProductOrderPopupList,
		codeField: 'productOrderNo',
		nameField: 'itemNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.compCd || '',
			filters?.workplaceCd || '',
			filters?.closeTag || '',
			filters?.orderDateFrom || '',
			filters?.orderDateTo || '',
		],

		searchFields: ['productOrderNo', 'itemCd', 'itemNm', 'workplaceNm'] as const,
		searchOptions: [
			{
				key: 'search',
				label: '검색어',
				type: 'input',
				placeholder: '작업지시번호 또는 품목',
				wrapperOption: {
					wrapperStyle: 'w-[310px] min-w-[310px] mb-[5px]',
					titleStyle: 'w-[100px] min-w-[100px]',
					inputStyle: 'w-[210px] min-w-[210px]',
				},
			},
			{
				key: 'compCd',
				label: '사업장',
				type: 'dropdown',
				dataKey: 'compCdList',
				wrapperOption: {
					wrapperStyle: 'w-[240px] min-w-[240px] mb-[5px]',
					titleStyle: 'w-[100px] min-w-[100px]',
					inputStyle: 'w-[140px] min-w-[140px]',
				},
			},
			{
				key: 'orderDate',
				label: '작업일자',
				type: 'dateRange',
				defaultDateRange: 'currentMonth',
			},
			{
				key: 'workplaceCd',
				label: '작업공정',
				type: 'dropdown',
				dataKey: 'workPlaceList',
				wrapperOption: {
					wrapperStyle: 'w-[240px] min-w-[240px] mb-[5px]',
					titleStyle: 'w-[100px] min-w-[100px]',
					inputStyle: 'w-[140px] min-w-[140px]',
				},
			},
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '작업지시번호', field: 'productOrderNo', width: 150 },
			{ headerName: '작업일자', field: 'orderDt', width: 100 },
			{ headerName: '품목코드', field: 'itemCd', width: 120 },
			{ headerName: '품목명', field: 'itemNm', flex: 2 },
			{ headerName: '규격', field: 'itemSpec', flex: 1 },
			{ headerName: '공정', field: 'workplaceNm', width: 120 },
			{ headerName: '지시수량', field: 'orderQty', width: 100 },
			{ headerName: '실적수량', field: 'resultQty', width: 100 },
			{ headerName: '마감', field: 'closeTag', width: 70 },
		] as const,

		modalSize: {
			size: 'md',
			width: 1400,
		},
	},
	{
		type: "productWork",
		titles: ["공정작업", "공정지시"] as const,
		apiFunction: getProductWorkList,
		codeField: "productWorkNo",
		nameField: "workplaceNm",
		getApiParams: (searchText: string) => [searchText || ""],
		searchFields: ["productWorkNo", "itemCd", "itemNm"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "공정지시번호", field: "productWorkNo", width: 180 },
			{ headerName: "작업공정", field: "workplaceNm", width: 120 },
			{ headerName: "품목코드", field: "itemCd", flex: 1 },
			{ headerName: "품목명", field: "itemNm", flex: 1 },
			{ headerName: "지시량", field: "orderQty", width: 110 },
		] as const,
		modalSize: { size: "sm", width: 1000 },
	},

	{
		type: "productWorkAll",
		titles: ["공정지시_모두", "과거공정", "현재공정"] as const,
		apiFunction: getProductWorkAllList,
		codeField: "productWorkNo",
		nameField: "workplaceNm",
		getApiParams: (searchText: string) => [searchText || ""],
		searchFields: ["productWorkNo", "itemCd", "itemNm"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "공정지시번호", field: "productWorkNo", width: 180 },
			{ headerName: "작업공정", field: "workplaceNm", width: 120 },
			{ headerName: "품목코드", field: "itemCd", flex: 1 },
			{ headerName: "품목명", field: "itemNm", flex: 1 },
			{ headerName: "지시량", field: "orderQty", width: 110 },
		] as const,
		modalSize: { size: "sm", width: 1000 },
	},
	// 생산실적
	{
		type: 'productResult',
		titles: ['생산지시','생산실적']as const,

		apiFunction: getProductResultPopupList,
		codeField: ['productOrderNo', 'productResultNo'] as string[],
		nameField: 'productOrderNo',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			{
				productOrderNo: filters?.productOrderNo || '',
				compCd: filters?.compCd || '',
				orderType: filters?.orderType || '',
				workplaceCd: filters?.workplaceCd || '',
				itemCd: filters?.itemCd || '',
				productEmp: filters?.productEmp || '',
				startDate: filters?.productDateFrom || '',
				endDate: filters?.productDateTo || '',
			},
		],

		searchFields: ['productResultNo', 'productOrderNo', 'itemCd'] as const,
		searchOptions: [
			{
				key: 'search',
				label: '검색어',
				type: 'input',
				placeholder: '생산지시번호 또는 품목코드',
				wrapperOption: {
					wrapperStyle: 'w-[310px] min-w-[310px] mb-[5px]',
					titleStyle: 'w-[100px] min-w-[100px]',
					inputStyle: 'w-[210px] min-w-[210px]',
				},
			},
			{
				key: 'compCd',
				label: '사업장',
				type: 'dropdown',
				dataKey: 'compCdList',
				wrapperOption: {
					wrapperStyle: 'w-[240px] min-w-[240px] mb-[5px]',
					titleStyle: 'w-[100px] min-w-[100px]',
					inputStyle: 'w-[140px] min-w-[140px]',
				},
			},
			{
				key: 'productDate',
				label: '생산일자',
				type: 'dateRange',
				defaultDateRange: 'currentMonth',
			},
			{
				key: 'workplaceCd',
				label: '작업공정',
				type: 'dropdown',
				dataKey: 'workPlaceList',
				wrapperOption: {
					wrapperStyle: 'w-[240px] min-w-[240px] mb-[5px]',
					titleStyle: 'w-[100px] min-w-[100px]',
					inputStyle: 'w-[140px] min-w-[140px]',
				},
			},
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '생산일자', field: 'productDt', width: 110 },
			{ headerName: '생산실적번호', field: 'productResultNo', width: 160 },
			{ headerName: '생산지시번호', field: 'productOrderNo', width: 160 },
			{ headerName: '사업장', field: 'compCd', width: 90 },
			{ headerName: '공정', field: 'workplaceCd', width: 100 },
			{ headerName: '품목코드', field: 'itemCd', width: 130 },
			{ headerName: '작업자', field: 'productEmp', width: 90 },
			{ headerName: '지시량', field: 'orderQty', width: 90 },
			{ headerName: '생산량', field: 'productQty', width: 90 },
			{ headerName: '불량수량', field: 'badQty', width: 90 },
			{ headerName: '실적량', field: 'acceptQty', width: 90 },
		] as const,

		modalSize: {
			size: 'md',
			width: 1400,
		},
	},
	{
		type: "afeeder",
		titles: ["Afeeder", "afeederCd", "afeeder"] as const,
		apiFunction: getAfeederCodeList,
		codeField: "minorCd",
		nameField: "codeNm",
		getApiParams: (searchText: string) => ["PIC_FEEDER", searchText],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				minorCd: item.minorCd,
				codeNm: item.codeNm,
			})),
		searchFields: ["codeNm", "minorCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "feeder코드", field: "minorCd", width: 100 },
			{ headerName: "feeder명", field: "codeNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},

	{
		type: "bfeeder",
		titles: ["Bfeeder", "bfeederCd", "bfeeder"] as const,
		apiFunction: getBfeederCodeList,
		codeField: "minorCd",
		nameField: "codeNm",
		getApiParams: (searchText: string) => ["PIC_FEEDER", searchText],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				minorCd: item.minorCd,
				codeNm: item.codeNm,
			})),
		searchFields: ["codeNm", "minorCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "feeder코드", field: "minorCd", width: 100 },
			{ headerName: "feeder명", field: "codeNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},

	{
		type: "cfeeder",
		titles: ["Cfeeder", "cfeederCd", "cfeeder"] as const,
		apiFunction: getCfeederCodeList,
		codeField: "minorCd",
		nameField: "codeNm",
		getApiParams: (searchText: string) => ["PIC_FEEDER", searchText],
		transformResponse: (data: any[]) =>
			data.map((item) => ({
				minorCd: item.minorCd,
				codeNm: item.codeNm,
			})),
		searchFields: ["codeNm", "minorCd"] as const,
		searchOptions: [{ key: "search", label: "검색어", type: "input" }],
		columns: [
			{ headerName: "순번", field: "rowIndex", width: 60 },
			{ headerName: "feeder코드", field: "minorCd", width: 100 },
			{ headerName: "feeder명", field: "codeNm", flex: 1 },
		] as const,
		modalSize: { size: "sm", width: 800 },
	},
] as const;

export const getCategoryByType = (type: CodeType): CategoryDefinition | undefined => {
	const CATEGORY_MAP = getCategoryMap();
	return CATEGORY_MAP.get(type);
};

let categoryMapCache: Map<CodeType, CategoryDefinition> | null = null;
const getCategoryMap = (): Map<CodeType, CategoryDefinition> => {
	if (!categoryMapCache) {
		categoryMapCache = new Map();
		CATEGORIES.forEach((category) => {
			categoryMapCache!.set(category.type, category);
		});
	}
	return categoryMapCache;
};

export const getCategoryByTitle = (title: string): CategoryDefinition | undefined => {
	return CATEGORIES.find((category) => category.titles.includes(title as any));
};
